import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import PatientInfoRow from 'components/Patient/PatientInfoRow';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import './style.scss';
import CalendarUtils from 'services/CalendarUtils';
import Icon from 'components/Utils/NewIcon';
import CPPUtils from 'services/CPPUtils';
import Collapse from 'components/Utils/Collapse';
import NewAdministeredDrug from 'components/HealthRecords/Immunizations/NewAdministeredDrug';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import MiscUtils from 'services/MiscUtils';

const AdministeredDrugsCardView = (props) => (
  <div className="drugs-card rounded">
    {!props.hideTopBar && <TopRow {...props} />}
    <CardContent {...props} />
  </div>
);

AdministeredDrugsCardView.propTypes = {
  patient: PropTypes.object.isRequired,
  hideTopBar: PropTypes.bool,
  onEdit: PropTypes.func,
  onReadminister: PropTypes.func,
  onDelete: PropTypes.func,
  onClose: PropTypes.func,
  form: PropTypes.string,
  drugs: PropTypes.array,
};

AdministeredDrugsCardView.defaultProps = {
  drugs: [],
};

const TopRow = (props) => (
  <div className="top-row row">
    <PatientInfoRow patient={props.patient} />
    <Icon src="xmark" className="close-button white" onClick={props.onClose} />
  </div>
);

const CardContent = (props) => (
  <div className="card-content">
    <div className="row">
      <span className="large-title padded-box">Administered Drugs</span>
      <ExportButton patient={props.patient} />
    </div>
    <div className="scroll-list">
      <AddDrug {...props} />
      <TableContent {...props} />
    </div>
  </div>
);

const AddDrug = (props) => (
  <Collapse
    title="Add Drug"
    collapsed={props.collapsed}
    onClick={() => props.setCollapsed(!props.collapsed)}
  >
    <div className="padded-box">
      <NewAdministeredDrug patient={props.patient} form={props.form} />
    </div>
  </Collapse>
);

const TableContent = (props) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [removing, setRemoving] = useState(false);

  function onClickedDelete(drugs) {
    setShowConfirmation(drugs);
  }

  return (
    <>
      {!!showConfirmation && (
        <ConfirmationDialog
          title="Remove Drug"
          message="Are you sure you want to remove this drug?"
          open={!!showConfirmation}
          onConfirm={async () => {
            setRemoving(true);
            await props.onDelete(showConfirmation);
            setRemoving(false);
            setShowConfirmation();
          }}
          loadingState={removing}
          onCancel={() => setShowConfirmation()}
        />
      )}
      <ReactTable
        key={props.drugs.length}
        data={props.drugs}
        defaultSorted={[
          {
            id: 'date',
            desc: false,
          },
        ]}
        columns={getColumns({ ...props, onClickedDelete })}
        defaultPageSize={props.drugs.length || 25}
        className="-striped -highlight"
      />
    </>
  );
};

function getColumns(props) {
  return [
    {
      Header: '',
      id: 'edit',
      maxWidth: 100,
      className: 'center-text',
      sortable: false,
      accessor: (v) => null,
      Cell: (row) => (
        <>
          <Icon
            src="pencil"
            alt="Edit"
            showTooltip={true}
            width="15px"
            onClick={() => props.onEdit(row.original)}
          />
          <Icon
            src="repeat"
            alt="Readminister"
            showTooltip={true}
            width="15px"
            className="ml-3"
            onClick={() => props.onReadminister(row.original)}
          />
          <Icon
            src="trash"
            alt="Remove"
            showTooltip={true}
            width="15px"
            className="ml-3"
            onClick={() => props.onClickedDelete(row.original)}
          />
        </>
      ),
    },
    {
      Header: '',
      show: false,
      id: 'date',
      accessor: (v) => v.value.given_date || v.value.scheduled_date,
    },
    {
      Header: 'Given Date',
      id: 'given_date',
      maxWidth: 120,
      minWidth: 120,
      accessor: (v) => v.value.given_date || v.value.scheduled_date,
      Cell: (row) => <DateCell drug={row.original} />,
    },
    {
      Header: 'Name',
      id: 'name',
      width: 'auto',
      accessor: (v) => v.value.name || '',
      Cell: (row) => <span title={row.value}>{row.value}</span>,
      className: 'center-text',
    },
    {
      Header: 'Description',
      id: 'description',
      width: 'auto',
      accessor: (v) => v.value.description || '',
      Cell: (row) => <span title={row.value}>{row.value}</span>,
      className: 'center-text',
    },
    {
      Header: 'Dosage',
      id: 'dosage',
      width: 'auto',
      accessor: (v) => v.value.dosage || '',
      Cell: (row) => <span title={row.value}>{row.value}</span>,
      className: 'center-text',
    },
    {
      Header: 'Site',
      id: 'site',
      width: 'auto',
      accessor: (v) => v.value.site || '',
      Cell: (row) => <span title={row.value}>{row.value}</span>,
      className: 'center-text',
    },
    {
      Header: 'Lot #',
      id: 'lot',
      width: 'auto',
      accessor: (v) => v.value.lot || '',
      Cell: (row) => <span title={row.value}>{row.value}</span>,
      className: 'center-text',
    },
    {
      Header: 'Route',
      id: 'route',
      width: 'auto',
      accessor: (v) => v.value.route || '',
      Cell: (row) => <span title={row.value}>{row.value}</span>,
      className: 'center-text',
    },
    {
      Header: 'Provider',
      id: 'provider',
      width: 'auto',
      accessor: (v) => v.value.given_by || '',
      Cell: (row) => <span title={row.value}>{row.value}</span>,
      className: 'center-text',
    },
  ];
}

const DateCell = ({ drug }) => {
  let {
    value: { given_date, scheduled_date },
  } = drug;
  let icon = CPPUtils.getImmunizationIcon(drug);
  let alt = CPPUtils.getImmunizationIconDescription(drug);
  return (
    <span className={'center-text'}>
      {icon.src ? <Icon {...icon} width="15px" alt={alt} /> : null}
      {given_date ? CalendarUtils.format(given_date) : CalendarUtils.format(scheduled_date)}
    </span>
  );
};

const ExportButton = ({ patient }) => {
  const linkRef = useRef();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);

  async function exportPdf() {
    setLoading(true);
    const result = await dispatch(
      api.HealthRecords.exportAdministeredDrugs({ patientId: patient.id }),
      { errorTitle: 'Administered Medications Export' },
    );

    if (!result) {
      return;
    }

    const url = MiscUtils.getBlobUrl(result);

    const a = linkRef.current;
    a.download = 'Administered Medications.pdf';
    a.href = url;
    a.click();
    a.href = '';
    setLoading(false);
  }

  return (
    <div className="export-button">
      <a href="careand.ca" style={{ display: 'none' }} ref={linkRef}>
        Download
      </a>
      <DefaultButton
        name="Export PDF"
        loading={loading}
        icon="file-pdf"
        onClick={exportPdf}
        style={{ width: '150px' }}
      />
    </div>
  );
};

export default AdministeredDrugsCardView;
